import './style.css';
import { useEffect, useState, useCallback } from "react";
import MenuTop from "../../components/menu/top/menu-top";
import Loader from "../../components/loader/loader";
import { fetchNotification, fetchMarkAsRead, fetchMarkAllAsRead, fetchUnreadNotificationsList } from "../../features/notifications/notification-service";
import { Notification } from "../../shared/interfaces/notification";
import Icons from '../../components/icons/iconts';
import { useAlert } from '../../components/alert/alert';
import { SEVERITY } from '../../shared/interfaces/alert';
import { useNotificationContext } from '../../shared/context/notification-context';

export default function NotificationCenter() {
    const [isLoading, setIsLoading] = useState(true);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [filter, setFilter] = useState('ALL');

    const { showAlert } = useAlert();
    const { updateUnreadCount, decrementUnreadCount } = useNotificationContext();

    const fetchNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetchNotification();
            
            if (response && response.notifications) {
                setNotifications(response.notifications);
            }
        } catch (error) {
            setNotifications([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchUnreadNotifications = useCallback(async () => {
        try {
            setIsLoading(true);
            const response = await fetchUnreadNotificationsList();
            
            if (response && response.notifications) {
                setNotifications(response.notifications);
            }
        } catch (error) {
            setNotifications([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const handleMarkAsRead = async (notification: Notification) => {
        try {
            await fetchMarkAsRead(notification.id);

            // Decrementa o contador apenas se a notificação não foi lida
            if (!notification.hasReaded) {
                decrementUnreadCount(1);
            }

            showAlert(`Notificação ${notification.name} marcada como lida`, SEVERITY.SUCCESS);
            fetchNotifications();
        } catch (error) {
            console.error('Erro ao marcar notificação como lida:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            const unreadNotifications = notifications.filter(n => !n.hasReaded);
            const markPromises = unreadNotifications.map(() => fetchMarkAllAsRead());
            
            await Promise.all(markPromises);

            // Decrementa o contador pelo número de notificações não lidas
            if (unreadNotifications.length > 0) {
                decrementUnreadCount(unreadNotifications.length);
            }

            fetchNotifications();

            showAlert(`Todas notificações marcadas como lidas`, SEVERITY.SUCCESS);
        } catch (error) {
            console.error('Erro ao marcar todas as notificações como lidas:', error);
        }
    };

    const handleFilterChange = (newFilter: string) => {
        setFilter(newFilter);
        if (newFilter === 'ALL') {
            fetchNotifications();
        } else {
            fetchUnreadNotifications();
        }
    };

    // Atualizar contador quando entrar na tela de notificações
    useEffect(() => {
        updateUnreadCount();
    }, [updateUnreadCount]);

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container-notification-center">
            <MenuTop />
            {isLoading && <Loader />}
            
            <div className="container-notification-center__content">
                <div className="notification-header">
                    <h1>Notificações</h1>
                </div>

                <div className="notification-controls">
                    <div className="filter-buttons">
                        <button 
                            className={`filter-btn ${filter === 'ALL' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('ALL')}
                        >
                            Todas
                        </button>
                        <button 
                            className={`filter-btn ${filter === 'UNREAD' ? 'active' : ''}`}
                            onClick={() => handleFilterChange('UNREAD')}
                        >
                            Não Lidas
                        </button>
                    </div>
                    
                    {notifications.some(n => !n.hasReaded) && (
                        <button 
                            className="mark-all-read-btn"
                            onClick={handleMarkAllAsRead}
                        >
                            Marcar todas como lidas
                        </button>
                    )}
                </div>

                <div className="notifications-list">
                    {notifications.length === 0 ? (
                        <div className="no-notifications">
                            <h3>Nenhuma notificação encontrada</h3>
                            <p>
                                {filter === 'ALL' 
                                    ? "Você não possui notificações no momento." 
                                    : "Você não possui notificações não lidas no momento."
                                }
                            </p>
                        </div>
                    ) : (
                        notifications.map(notification => (
                            <div 
                                key={notification.id} 
                                className={`notification-item ${!notification.hasReaded ? 'unread' : ''}`}
                                style={{ width: '100%' }}
                            >
                                <div className="notification-item-content">
                                    <div className="notification-left">   
                                        <h3 className="notification-title">{notification.name}</h3>
                                        <p className="notification-description">{notification.description}</p>
                                        <span className="notification-date">{formatDate(notification.scheduledDate)}</span>
                                    </div>
                                    
                                    <div className="notification-right">
                                        <div className="notification-meta">
                                            {!notification.hasReaded && (
                                                <button className="btn-mark-read" 
                                                    onClick={() => handleMarkAsRead(notification)}>
                                                    Marcar como lida
                                                    <Icons iconName="Done" fontSize={16} color="#6c757d" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}